import React, { useState, useEffect, useMemo } from "react";
import api from "./services/api";
import "react-toastify/dist/ReactToastify.css";
import { QueryClient, QueryClientProvider } from "react-query";
import { ptBR } from "@material-ui/core/locale";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import ColorModeContext from "./layout/themeContext";
import { ActiveMenuProvider } from "./context/ActiveMenuContext";
import Favicon from "react-favicon";
import { getBackendUrl } from "./config";
import Routes from "./routes";
import { SocketContext, socketManager } from "./context/Socket/SocketContext";
import defaultLogoLight from "./assets/logo.png";
import defaultLogoDark from "./assets/logo-black.png";
import defaultLogoFavicon from "./assets/favicon.ico";
import useSettings from "./hooks/useSettings";
import { replaceToastSuccess } from "./errors/toastSuccess";
import { replaceToastError, replaceToastWarnInfo } from "./errors/toastError";
import { AudioPlayerProvider } from "./context/AudioPlayerContext";
import * as sw from "./serviceWorker";

const packageVersion = require("../package.json").version;

const queryClient = new QueryClient();

const App = () => {
  const [locale, setLocale] = useState();
  const appColorLocalStorage = localStorage.getItem("primaryColorLight") || localStorage.getItem("primaryColorDark") || "#065183";
  const appNameLocalStorage = localStorage.getItem("appName") || "";
  const storedChoice = (() => {
    try { return window.localStorage.getItem("themeChoice"); } catch { return null; }
  })();
  const initialChoice = storedChoice || "auto";
  const initialMode = (() => {
    if (initialChoice !== "auto") return initialChoice;
    try {
      return (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) ? 'dark' : 'light';
    } catch { return 'light'; }
  })();
  const [themeChoice, setThemeChoice] = useState(initialChoice);
  const [mode, setMode] = useState(initialMode);
  const [primaryColorLight, setPrimaryColorLight] = useState(appColorLocalStorage);
  const [primaryColorDark, setPrimaryColorDark] = useState(appColorLocalStorage);
  const [appLogoLight, setAppLogoLight] = useState(defaultLogoLight);
  const [appLogoDark, setAppLogoDark] = useState(defaultLogoDark);
  const [appLogoFavicon, setAppLogoFavicon] = useState(defaultLogoFavicon);
  const [appName, setAppName] = useState(appNameLocalStorage);
  const { getPublicSetting } = useSettings();

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setThemeChoice((prevChoice) => {
          let base = prevChoice;
          if (prevChoice === 'auto') {
            try { base = (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) ? 'dark' : 'light'; } catch { base = mode; }
          }
          const next = base === 'light' ? 'dark' : 'light';
          setMode(next);
          try { window.localStorage.setItem('themeChoice', next); } catch {}
          try { window.localStorage.setItem('preferredTheme', next); } catch {}
          return next;
        });
      },
      applyThemeChoice: (choice) => {
        try {
          const effective = choice === 'auto'
            ? ((window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) ? 'dark' : 'light')
            : choice;
          setThemeChoice(choice);
          setMode(effective);
          try { window.localStorage.setItem('themeChoice', choice); } catch {}
          try { window.localStorage.setItem('preferredTheme', effective); } catch {}
        } catch {}
      },
      setPrimaryColorLight,
      setPrimaryColorDark,
      setAppLogoLight,
      setAppLogoDark,
      setAppLogoFavicon,
      setAppName,
      appLogoLight,
      appLogoDark,
      appLogoFavicon,
      appName,
      mode,
      themeChoice,
    }),
    [appLogoLight, appLogoDark, appLogoFavicon, appName, mode, themeChoice]
  );

  const theme = useMemo(
    () =>
      createTheme(
        {          scrollbarStyles: {
            "&::-webkit-scrollbar": {
              width: "8px",
              height: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: mode === "light" ? "rgba(0, 0, 0, 0.05)" : "rgba(255, 255, 255, 0.05)",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: mode === "light" 
                ? `linear-gradient(135deg, ${primaryColorLight}CC, ${primaryColorLight}88)`
                : `linear-gradient(135deg, ${primaryColorDark}CC, ${primaryColorDark}88)`,
              borderRadius: "10px",
              border: "1px solid transparent",
              backgroundClip: "padding-box",
              "&:hover": {
                background: mode === "light" 
                  ? `linear-gradient(135deg, ${primaryColorLight}, ${primaryColorLight}DD)`
                  : `linear-gradient(135deg, ${primaryColorDark}, ${primaryColorDark}DD)`,
              },
              "&:active": {
                background: mode === "light" ? primaryColorLight : primaryColorDark,
              }
            },
            "&::-webkit-scrollbar-corner": {
              background: "transparent",
            }
          },          scrollbarStylesSoft: {
            "&::-webkit-scrollbar": {
              width: "6px",
              height: "6px",
            },
            "&::-webkit-scrollbar-track": {
              background: mode === "light" ? "rgba(0, 0, 0, 0.03)" : "rgba(255, 255, 255, 0.03)",
              borderRadius: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: mode === "light" 
                ? "linear-gradient(135deg, #E0E0E0, #BDBDBD)"
                : "linear-gradient(135deg, #424242, #303030)",
              borderRadius: "8px",
              border: "1px solid transparent",
              backgroundClip: "padding-box",
              "&:hover": {
                background: mode === "light" 
                  ? "linear-gradient(135deg, #BDBDBD, #9E9E9E)"
                  : "linear-gradient(135deg, #515151, #424242)",
              }
            },
            "&::-webkit-scrollbar-corner": {
              background: "transparent",
            }
          },
          scrollbarStylesSoftBig: {
            "&::-webkit-scrollbar": {
              width: "12px",
              height: "12px",
            },
            "&::-webkit-scrollbar-track": {
              background: mode === "light" ? "rgba(0, 0, 0, 0.05)" : "rgba(255, 255, 255, 0.05)",
              borderRadius: "12px",
              margin: "2px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: mode === "light" 
                ? "linear-gradient(135deg, #E0E0E0, #BDBDBD)"
                : "linear-gradient(135deg, #424242, #303030)",
              borderRadius: "12px",
              border: "2px solid transparent",
              backgroundClip: "padding-box",
              "&:hover": {
                background: mode === "light" 
                  ? "linear-gradient(135deg, #BDBDBD, #9E9E9E)"
                  : "linear-gradient(135deg, #515151, #424242)",
              }
            },
            "&::-webkit-scrollbar-corner": {
              background: "transparent",
            }
          },
          palette: {
            type: mode,
            mode: mode,
            primary: { main: mode === "light" ? primaryColorLight : primaryColorDark },
            textPrimary: mode === "light" ? primaryColorLight : primaryColorDark,
            borderPrimary: mode === "light" ? primaryColorLight : primaryColorDark,
            dark: { main: mode === "light" ? "#333333" : "#F3F3F3" },
            light: { main: mode === "light" ? "#F3F3F3" : "#333333" },
            fontColor: mode === "light" ? primaryColorLight : primaryColorDark,
            tabHeaderBackground: mode === "light" ? "#EEE" : "#666",
            optionsBackground: mode === "light" ? "#fafafa" : "#333",
            fancyBackground: mode === "light" ? "#fafafa" : "#333",
            total: mode === "light" ? "#fff" : "#222",
            messageIcons: mode === "light" ? "grey" : "#F3F3F3",
            inputBackground: mode === "light" ? "#FFFFFF" : "#333",
            barraSuperior: mode === "light" ? primaryColorLight : "#666",
          },
          mode,
          appLogoLight,
          appLogoDark,
          appLogoFavicon,
          appName,
          calculatedLogoDark: () => {
            if (appLogoDark === defaultLogoDark && appLogoLight !== defaultLogoLight) {
              return appLogoLight;
            }
            return appLogoDark;
          },
          calculatedLogoLight: () => {
            if (appLogoDark !== defaultLogoDark && appLogoLight === defaultLogoLight) {
              return appLogoDark;
            }
            return appLogoLight;
          },
        },
        locale
      ),
    [appLogoLight, appLogoDark, appLogoFavicon, appName, locale, mode, primaryColorDark, primaryColorLight]
  );

  useEffect(() => {
    const i18nlocale = localStorage.getItem("i18nextLng");
    const browserLocale = i18nlocale.substring(0, 2) + i18nlocale.substring(3, 5);

    if (browserLocale === "ptBR") {
      setLocale(ptBR);
    }
  }, []);

  // Ativar padronização automática dos toasts de sucesso
  useEffect(() => {
    replaceToastSuccess();
    replaceToastError();
    replaceToastWarnInfo();
  }, []);

  // Atualização automática do frontend (Modo B):
  // - Sem UI; compara versão embutida no bundle com a armazenada; registra e recarrega 1x
  useEffect(() => {
    (async () => {
      try {
        const stored = window.localStorage.getItem("version") || "0.0.0";
        const alreadyReloaded = sessionStorage.getItem("didAutoUpdateReload");
        const cmp = (a, b) => {
          try {
            const pa = String(a).split('.').map(n => parseInt(n, 10) || 0);
            const pb = String(b).split('.').map(n => parseInt(n, 10) || 0);
            const len = Math.max(pa.length, pb.length);
            for (let i = 0; i < len; i++) {
              const va = pa[i] || 0; const vb = pb[i] || 0;
              if (va > vb) return 1; if (va < vb) return -1;
            }
            return 0;
          } catch { return a === b ? 0 : (String(a) > String(b) ? 1 : -1); }
        };

        if (stored !== packageVersion && cmp(packageVersion, stored) === 1) {
          // Só avança o backend se o bundle atual for mais novo que o salvo
          try { window.localStorage.setItem("version", packageVersion); } catch {}
          try { await api.post("/version", { version: packageVersion }); } catch {}
          if (!alreadyReloaded) {
            try { sessionStorage.setItem("didAutoUpdateReload", "1"); } catch {}
            try {
              const reg = await sw.getServiceWorkerRegistration();
              if (reg && reg.update) { try { await reg.update(); } catch {} }
            } catch {}
            setTimeout(() => { window.location.reload(); }, 100);
          }
        }
      } catch (_) {}
    })();
  }, []);

  // Detector para abas que ficam abertas: ao voltar ao foco/visibilidade, verifica backend
  // e recarrega se houver versão mais nova publicada.
  useEffect(() => {
    let cancelled = false;
    let debounceId = null;
    let checking = false;

    const cmp = (a, b) => {
      try {
        const pa = String(a).split('.').map(n => parseInt(n, 10) || 0);
        const pb = String(b).split('.').map(n => parseInt(n, 10) || 0);
        const len = Math.max(pa.length, pb.length);
        for (let i = 0; i < len; i++) {
          const va = pa[i] || 0; const vb = pb[i] || 0;
          if (va > vb) return 1; if (va < vb) return -1;
        }
        return 0;
      } catch { return a === b ? 0 : (String(a) > String(b) ? 1 : -1); }
    };

    const checkAndReloadIfNeeded = async () => {
      if (checking || cancelled) return;
      checking = true;
      try {
        // Tenta atualizar o SW antes de checar a versão
        try {
          const reg = await sw.getServiceWorkerRegistration();
          if (reg && reg.update) { try { await reg.update(); } catch {} }
        } catch {}

        const { data } = await api.get('/version');
        const backendVersion = data && data.version;
        const stored = window.localStorage.getItem('version') || '0.0.0';
        if (backendVersion && cmp(backendVersion, stored) === 1) {
          if (!sessionStorage.getItem('didAutoUpdateReload')) {
            try { sessionStorage.setItem('didAutoUpdateReload', '1'); } catch {}
            // Não altera localStorage aqui para evitar conflito; só recarrega
            window.location.reload();
          }
        }
      } catch (_) {
        // silencia
      } finally {
        checking = false;
      }
    };

    const schedule = () => {
      if (debounceId) clearTimeout(debounceId);
      debounceId = setTimeout(() => { checkAndReloadIfNeeded(); }, 400);
    };

    const onVisibility = () => { if (document.visibilityState === 'visible') schedule(); };
    const onFocus = () => schedule();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);

    // Verificação periódica opcional a cada 15 minutos
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') checkAndReloadIfNeeded();
    }, 15 * 60 * 1000);

    return () => {
      cancelled = true;
      if (debounceId) clearTimeout(debounceId);
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  useEffect(() => {
    try { window.localStorage.setItem("preferredTheme", mode); } catch {}
  }, [mode]);

  // Sincronizar com tema do sistema (Windows/macOS) quando a escolha for "auto"
  useEffect(() => {
    if (themeChoice !== 'auto') return;
    let mql;
    const applySystem = (isDark) => {
      setMode(isDark ? 'dark' : 'light');
    };
    try {
      if (window.matchMedia) {
        mql = window.matchMedia('(prefers-color-scheme: dark)');
        // Aplicar imediatamente o estado atual do sistema
        applySystem(!!mql.matches);
        const handler = (e) => applySystem(!!(e.matches));
        if (typeof mql.addEventListener === 'function') {
          mql.addEventListener('change', handler);
          return () => {
            try { mql.removeEventListener('change', handler); } catch {}
          };
        } else if (typeof mql.addListener === 'function') {
          mql.addListener(handler);
          return () => {
            try { mql.removeListener(handler); } catch {}
          };
        }
      }
    } catch (_) {}
    return undefined;
  }, [themeChoice]);

  useEffect(() => {
    try {
      const be = getBackendUrl();
      if (be) localStorage.setItem('public:backendUrl', be);
    } catch (_) {}
    
    getPublicSetting("primaryColorLight")
      .then((color) => {
        setPrimaryColorLight(color || "#0000FF");
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("primaryColorDark")
      .then((color) => {
        setPrimaryColorDark(color || "#39ACE7");
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appLogoLight")
      .then((file) => {
        setAppLogoLight(file ? getBackendUrl() + "/public/" + file : defaultLogoLight);
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appLogoDark")
      .then((file) => {
        setAppLogoDark(file ? getBackendUrl() + "/public/" + file : defaultLogoDark);
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appLogoFavicon")
      .then((file) => {
        setAppLogoFavicon(file ? getBackendUrl() + "/public/" + file : defaultLogoFavicon);
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appName")
      .then((name) => {
        setAppName(name || "Plataforma WhatsApp");
        try { if (name) localStorage.setItem('public:appName', name); } catch (_) {}
      })
      .catch((error) => {
        console.log("!==== Erro ao carregar temas: ====!", error);
        setAppName("Plataforma WhatsApp");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--primaryColor", mode === "light" ? primaryColorLight : primaryColorDark);
  }, [primaryColorLight, primaryColorDark, mode]);

  useEffect(() => {
    async function fetchVersionData() {
      try {
        const response = await api.get("/version");
        const { data } = response;
        window.localStorage.setItem("frontendVersion", data.version);
      } catch (error) {
        console.log("Error fetching data", error);
      }
    }
    fetchVersionData();
  }, []);

  return (
    <>
      <Favicon url={appLogoFavicon ? getBackendUrl() + "/public/" + appLogoFavicon : defaultLogoFavicon} />
      <ColorModeContext.Provider value={{ colorMode }}>
        <ThemeProvider theme={theme}>
          <QueryClientProvider client={queryClient}>
            <ActiveMenuProvider>
              <AudioPlayerProvider>
                <SocketContext.Provider value={socketManager}>
                  <Routes />
                </SocketContext.Provider>
              </AudioPlayerProvider>
            </ActiveMenuProvider>
          </QueryClientProvider>
        </ThemeProvider>
      </ColorModeContext.Provider>
    </>
  );
};

export default App;
